import { useState } from "react";

import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import EastOutlined from "@mui/icons-material/EastOutlined";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { green } from "@mui/material/colors";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount || 0);

const formatDate = (dateStr) => (dateStr ? new Date(dateStr).toLocaleDateString() : "");

export const PaymentDialog = ({
  open,
  onClose,
  onSnooze,
  onPayment,
  onPaperCheck,
  openInvoices,
  isLoadingPaymentLink,
  isLoadingPaperCheck,
}) => {
  const [selectedIds, setSelectedIds] = useState(new Set());

  const handleToggle = (invoiceNumber) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceNumber)) {
        next.delete(invoiceNumber);
      } else {
        next.add(invoiceNumber);
      }
      return next;
    });
  };

  const invoices = openInvoices || [];
  const selectedInvoices = invoices.filter((s) => selectedIds.has(s.invoicenumber));
  const payTotal = selectedInvoices.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);
  const hasSelection = selectedInvoices.length > 0;

  return (
    <Dialog open={open} PaperProps={{ sx: { minWidth: "60vw" } }}>
      <DialogTitle>Your Tilley Invoices</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          The following invoices are due. Select the invoices you would like to pay.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Invoice #</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Due Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((sale) => (
              <TableRow
                key={sale.invoicenumber}
                hover
                onClick={() => handleToggle(sale.invoicenumber)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell padding="checkbox">
                  <Checkbox checked={selectedIds.has(sale.invoicenumber)} onClick={(e) => e.stopPropagation()} onChange={() => handleToggle(sale.invoicenumber)} />
                </TableCell>
                <TableCell>{sale.invoicenumber}</TableCell>
                <TableCell>{sale.description}</TableCell>
                <TableCell align="right">{formatCurrency(sale.total)}</TableCell>
                <TableCell>{formatDate(sale.duedate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Typography variant="h6">Pay Total: {formatCurrency(payTotal)}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onPaperCheck(selectedInvoices)}
          disabled={isLoadingPaperCheck || !hasSelection}
        >
          Paper Check
          {isLoadingPaperCheck ? (
            <CircularProgress size={24} sx={{ color: green[500], ml: 1 }} />
          ) : (
            <EastOutlined sx={{ ml: 1 }} />
          )}
        </Button>
        <Button
          variant="contained"
          onClick={() => onPayment(selectedInvoices)}
          disabled={isLoadingPaymentLink || !hasSelection}
        >
          Pay by Card
          {isLoadingPaymentLink ? (
            <CircularProgress size={24} sx={{ color: green[500], ml: 1 }} />
          ) : (
            <EastOutlined sx={{ ml: 1 }} />
          )}
        </Button>
        <Button variant="outlined" onClick={onSnooze}>
          Remind Me Later
          <CalendarMonthOutlined sx={{ ml: 1 }} />
        </Button>
        <Button variant="outlined" onClick={onClose}>
          Close
          <CloseOutlined sx={{ ml: 1 }} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
